import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../contextos/AuthContext";
import { estilosGrupos } from "../elementos/gruposEstilos";
import {
  cargarGruposProfesor,
  crearGrupo,
  resumenMiembros,
} from "../funciones/gruposServicio";

import ModalCrearGrupo from "./dashboard_profesor/ModalCrearGrupo";
import ModalInvitar from "./dashboard_profesor/ModalInvitar";
import PanelAdministrarGrupo from "./dashboard_profesor/PanelAdministrarGrupo";

export default function GruposProfesor() {
  const navigate = useNavigate();
  const { usuario } = useAuth();

  const [grupos, setGrupos] = useState([]);
  const [resumenes, setResumenes] = useState({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [idInvitar, setIdInvitar] = useState(null);
  const [idAdministrar, setIdAdministrar] = useState(null);

  const cargar = useCallback(async () => {
    if (!usuario) return;

    setError("");

    try {
      const lista = await cargarGruposProfesor(usuario.uid);
      setGrupos(lista);

      const conteos = await Promise.all(
        lista.map(async (g) => [g.id, await resumenMiembros(g.id)])
      );
      setResumenes(Object.fromEntries(conteos));
    } catch (err) {
      console.error("Error cargando grupos:", err);
      setError("No fue posible cargar tus grupos. Revisa la conexión e inténtalo de nuevo.");
    } finally {
      setCargando(false);
    }
  }, [usuario]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardarGrupo = async (datos) => {
    await crearGrupo(datos, usuario.uid);
    setMostrarCrear(false);
    await cargar();
  };

  // Siempre se toma la versión más reciente del grupo
  const grupoInvitar = grupos.find((g) => g.id === idInvitar) || null;
  const grupoAdministrar = grupos.find((g) => g.id === idAdministrar && g.esAdmin) || null;

  return (
    <div className="gg gg-pagina">
      <style>{estilosGrupos}</style>

      <header className="gg-encabezado">
        <div>
          <h1>Mis grupos</h1>
          <p className="gg-sutil">Grupos que administras o acompañas como profesor.</p>
        </div>
        <button type="button" className="gg-btn gg-btn--primario" onClick={() => setMostrarCrear(true)}>
          + Nuevo grupo
        </button>
      </header>

      {error && <p role="alert" className="gg-alerta">{error}</p>}

      {cargando && <p className="gg-sutil">Cargando grupos…</p>}

      {!cargando && !error && grupos.length === 0 && (
        <div className="gg-vacio">
          <p style={{ marginBottom: 12 }}>Aún no tienes grupos. Crea el primero e invita a tus estudiantes con un enlace.</p>
          <button type="button" className="gg-btn gg-btn--primario" onClick={() => setMostrarCrear(true)}>
            Crear mi primer grupo
          </button>
        </div>
      )}

      {grupos.length > 0 && (
        <div className="gg-grid">
          {grupos.map((grupo) => {
            const resumen = resumenes[grupo.id];
            return (
              <article key={grupo.id} className="gg-tarjeta">
                <div className="gg-tarjeta__cabecera">
                  <h2 className="gg-tarjeta__nombre">{grupo.nombre}</h2>
                  <span className={`gg-insignia ${grupo.esAdmin ? "gg-insignia--admin" : ""}`}>
                    {grupo.esAdmin ? "Administrador" : "Profesor"}
                  </span>
                </div>

                {grupo.descripcion && <p className="gg-sutil">{grupo.descripcion}</p>}

                {grupo.examenes?.length > 0 && (
                  <div className="gg-etiquetas">
                    {grupo.examenes.map((examen) => (
                      <span key={examen} className="gg-insignia">{examen}</span>
                    ))}
                  </div>
                )}

                <div className="gg-cifras">
                  <span><strong>{resumen?.estudiantes ?? "–"}</strong> estudiantes</span>
                  <span><strong>{grupo.profesores?.length ?? 1}</strong> {grupo.profesores?.length === 1 ? "profesor" : "profesores"}</span>
                  {grupo.esAdmin && resumen?.pendientes > 0 && (
                    <span className="gg-insignia gg-insignia--aviso">
                      {resumen.pendientes} {resumen.pendientes === 1 ? "solicitud" : "solicitudes"}
                    </span>
                  )}
                </div>

                <div className="gg-tarjeta__acciones">
                  <button
                    type="button"
                    className="gg-btn gg-btn--primario"
                    onClick={() => navigate(`/profesor/grupos/${grupo.id}`)}
                  >
                    Ver seguimiento
                  </button>
                  <button type="button" className="gg-btn" onClick={() => setIdInvitar(grupo.id)}>
                    Invitar
                  </button>
                  {grupo.esAdmin && (
                    <button type="button" className="gg-btn" onClick={() => setIdAdministrar(grupo.id)}>
                      Administrar
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <ModalCrearGrupo
        abierto={mostrarCrear}
        onCerrar={() => setMostrarCrear(false)}
        onGuardar={guardarGrupo}
      />

      <ModalInvitar
        grupo={grupoInvitar}
        abierto={!!grupoInvitar}
        onCerrar={() => setIdInvitar(null)}
      />

      <PanelAdministrarGrupo
        grupo={grupoAdministrar}
        abierto={!!grupoAdministrar}
        onCerrar={() => setIdAdministrar(null)}
        onCambio={cargar}
      />
    </div>
  );
}